// src/cmps/TextEditor/LexicalTextEditor.jsx
import React, { useEffect, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HeadingNode } from '@lexical/rich-text'
import { ParagraphNode, TextNode, $getRoot, $createParagraphNode } from 'lexical'
import { LinkNode } from '@lexical/link'
import { $generateNodesFromDOM, $generateHtmlFromNodes } from '@lexical/html'
import { ToolbarPlugin } from './ToolbarPlugin'
import { LinkPlugin } from './plugins/LinkPlugin'
import { FloatingLinkEditor } from './plugins/FloatingLinkEditor'
import { FloatingTextToolbar } from './plugins/FloatingTextToolbar'
import { KeyBindingsPlugin } from './plugins/KeyBindingsPlugin'
import { DragDropUploadPlugin } from './plugins/DragDropUploadPlugin'
import { DragHandlePlugin } from './plugins/DragHandlePlugin'
import { EmbedPlugin } from './plugins/EmbedPlugin'
import { ImageNode } from './nodes/ImageNode'
import { EmbedNode } from './nodes/EmbedNode'
import { lexicalTheme } from './theme/lexical-theme'
import { DraftPlugin } from './plugins/DraftPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'

export function LexicalTextEditor({
  onChange,
  initialContent,
  shouldLoadDraft = false,
  editorRefCallback,
}) {
  const contentRef = useRef(null)
  const editorContainerRef = useRef(null)

  const initialConfig = {
    namespace: 'AniZoneEditor',
    theme: lexicalTheme,
    onError: console.error,
    nodes: [
      HeadingNode,
      ParagraphNode,
      TextNode,
      LinkNode,
      ImageNode,
      EmbedNode,
    ],
    editorState: (editor) => {
      if (initialContent && !shouldLoadDraft) {
        const dom = new DOMParser().parseFromString(initialContent, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)

        editor.update(() => {
          const root = $getRoot()
          root.clear()

          const adjustedNodes = []

          for (const node of nodes) {
            const nodeType = node.getType()

            if (nodeType === 'text') {
              if (node.getTextContent().trim() === '') {
                continue
              }

              const paragraphNode = $createParagraphNode()
              paragraphNode.append(node)
              adjustedNodes.push(paragraphNode)
            } else if (nodeType === 'linebreak') {
              continue
            } else {
              adjustedNodes.push(node)
            }
          }

          root.append(...adjustedNodes)
        })
      }
    },
  }

  return (
    <section
      ref={editorContainerRef}
      className="relative rounded-md mt-10 bg-zinc-900 border border-zinc-700 flex flex-col h-[700px] p-4"
    >
      <LexicalComposer initialConfig={initialConfig}>
        <EditorStateWatcher
          onChange={onChange}
          editorRefCallback={editorRefCallback}
        />
        <ToolbarPlugin />
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto px-6 py-4 relative"
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="editor min-h-full outline-none text-white text-sm leading-7" />
            }
            placeholder={
              <div className="absolute top-16 right-6 text-zinc-500 pointer-events-none">
                כתוב כאן את התוכן שלך…
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />

          {/* העבר את ה-contentRef לפלאגין הקישור */}
          <LinkPlugin anchorElem={contentRef} />

          <EmbedPlugin />
          {/* <FloatingLinkEditor anchorElem={contentRef} /> */}
          <FloatingTextToolbar anchorElem={contentRef} />
          <KeyBindingsPlugin />
          {shouldLoadDraft && <DraftPlugin />}
          <DragDropUploadPlugin />
          <DragHandlePlugin anchorElem={contentRef.current} />
        </div>
      </LexicalComposer>
    </section>
  )
}

function EditorStateWatcher({ onChange, editorRefCallback }) {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor)
        const text = $getRoot().getTextContent()
        onChange?.(html, text)
        editorRefCallback?.({ getHTML: () => html, getText: () => text })
      })
    })
  }, [editor, onChange, editorRefCallback])
  return null
}
